import "antd/dist/antd.css";
import "../App.css";

import { Alert, Button, Collapse, Layout, Space, Typography } from "antd";
import {
    HomeOutlined,
    LoginOutlined,
    ReloadOutlined,
} from "@ant-design/icons";
import {
    isRouteErrorResponse,
    useLocation,
    useNavigate,
    useRouteError,
} from "react-router-dom";
import { goToApp } from "../siteLinks";

const { Content } = Layout;
const { Paragraph, Text } = Typography;

const STATUS_COPY: Record<number, { title: string; help: string }> = {
    401: {
        title: "You're signed out",
        help: "Your session has expired. Sign in again to continue.",
    },
    403: {
        title: "You don't have access to this tour",
        help: "Either the owner removed you, the share code changed, or this tour belongs to someone else. Ask the owner for the share code to rejoin.",
    },
    404: {
        title: "We couldn't find this tour",
        help: "It may have been deleted, or the link is wrong. Head back home to see your tours.",
    },
    500: {
        title: "Something went wrong on our end",
        help: "The server hit an error loading this page. Try again in a moment.",
    },
};

const extractStatus = (error: any): number | null => {
    if (isRouteErrorResponse(error)) return error.status;
    if (typeof error?.status === "number") return error.status;
    const text = typeof error?.message === "string" ? error.message : "";
    const match = text.match(/\b(4\d\d|5\d\d)\b/);
    return match ? Number(match[1]) : null;
};

function RouteError() {
    const error = useRouteError() as any;
    const navigate = useNavigate();
    const location = useLocation();

    const status = extractStatus(error);
    const copy = (status && STATUS_COPY[status]) || {
        title: "Something went wrong loading this page",
        help: "Try reloading. If it keeps happening, sign out and back in.",
    };

    const technical =
        (error?.stack as string | undefined) ||
        (typeof error?.message === "string" ? error.message : null) ||
        (typeof error === "string" ? error : null) ||
        (() => {
            try { return JSON.stringify(error, null, 2); } catch { return null; }
        })();

    return (
        <div className="App">
            <Layout>
                <Content style={{ padding: "48px 24px", maxWidth: 720, margin: "0 auto" }}>
                    <Alert
                        type="error"
                        showIcon
                        title={copy.title}
                        description={
                            <>
                                <Paragraph style={{ marginBottom: 8 }}>{copy.help}</Paragraph>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {status ? `HTTP ${status} · ` : ""}{location.pathname}
                                </Text>
                            </>
                        }
                        style={{ marginBottom: 16 }}
                    />

                    <Space wrap>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => window.location.reload()}
                        >
                            Reload
                        </Button>
                        <Button
                            icon={<HomeOutlined />}
                            onClick={() => navigate("/")}
                        >
                            Go home
                        </Button>
                        <Button
                            type="primary"
                            icon={<LoginOutlined />}
                            onClick={() => goToApp("/login")}
                        >
                            Sign in
                        </Button>
                    </Space>

                    {technical && (
                        <Collapse
                            ghost
                            style={{ marginTop: 24 }}
                            items={[{
                                key: "details",
                                label: "Technical details",
                                children: (
                                    <pre style={{
                                        background: "var(--gt-surface-muted, #f6f6f6)",
                                        border: "1px solid #eee",
                                        borderRadius: 6,
                                        padding: 12,
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                        fontSize: 12,
                                        margin: 0,
                                    }}>
                                        {technical}
                                    </pre>
                                ),
                            }]}
                        />
                    )}
                </Content>
            </Layout>
        </div>
    );
}

export default RouteError;
